/**
 * Help & resources dropdown in the top nav (HelpCircle "?" icon next to the bell).
 *
 * Companion to the bottom-right `GetStartedChecklist` — the checklist tracks
 * the user's actual product progress (pair screen, create content, etc.); this
 * menu holds the static resources (docs, guide, book a call). Style mirrors
 * Claude / Notion / Linear app menus: compact rows, muted lucide icon on the
 * left, label in the middle, external-link arrow on the right. No card chips,
 * no gradients — just a clean menu.
 *
 * Data comes from the same `getAppCfgs.getStarts` backend list the legacy
 * dropdown used, so URL/copy changes from Marketing propagate without a
 * frontend deploy.
 */

import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  BookOpen,
  ExternalLink,
  GraduationCap,
  Headphones,
  PlayCircle,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import { GET_APP_CFG } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
import { WistiaDialog } from '@/components/shared';
import { GETTING_STARTED_VIDEO_ID } from '@/features/get-started/constants';

interface GetStartedItem {
  text: string;
  subtext: string;
  button: string;
  url: string;
  type?: 'youtube' | 'link';
}

interface AppCfgResponse {
  getAppCfgs: {
    getStarts?: GetStartedItem[];
  } | null;
}

/** localStorage key that tracks whether the user has opened the help menu at
 *  least once. Used to drive the "new"-indicator dot on the ? icon — once
 *  flipped, the dot disappears for that browser. */
const HELP_DROPDOWN_SEEN_KEY = 'scio3-help-dropdown-seen';

/** The backend returns ~4 items (incl. a YouTube demo we don't want). We pluck
 *  the 3 we care about by keyword match against the text label. */
function findItem(items: GetStartedItem[], keyword: string): GetStartedItem | undefined {
  const lower = keyword.toLowerCase();
  return items.find((item) => item.text.toLowerCase().includes(lower) && item.type !== 'youtube');
}

export function HelpDropdown() {
  const { t } = useTranslation();
  // Track whether the user has opened the help menu at least once. Backed by
  // localStorage so it persists across reloads — the unseen dot disappears
  // permanently for that browser after the first open.
  const [hasSeen, setHasSeen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HELP_DROPDOWN_SEEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  // Controls the Wistia modal for the "Watch demo" menu item — null hides
  // the dialog, GETTING_STARTED_VIDEO_ID opens it. Same pattern the
  // bottom-right checklist uses for its "Watch our getting started video"
  // footer link, so both surfaces share the same player.
  const [videoId, setVideoId] = useState<string | null>(null);

  const { data } = useQuery<AppCfgResponse>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId,
  });

  const items = data?.getAppCfgs?.getStarts ?? [];
  const stepByStep = findItem(items, 'step by step') ?? findItem(items, 'getting started');
  const documentation = findItem(items, 'documentation');
  const bookACall = findItem(items, 'still need help') ?? findItem(items, 'book a call');

  // Mark as seen on first open so the unseen-dot disappears. Persist to
  // localStorage in the same handler so a hard refresh doesn't bring it back.
  const handleOpenChange = (next: boolean) => {
    if (next && !hasSeen) {
      setHasSeen(true);
      try {
        localStorage.setItem(HELP_DROPDOWN_SEEN_KEY, '1');
      } catch {
        // Persistence is best-effort.
      }
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="help-dropdown-trigger"
                variant="ghost"
                size="icon-sm"
                className={cn(
                  'relative shrink-0 overflow-visible text-muted-foreground hover:text-foreground',
                  // Match its sibling NotificationDropdown. This was the only top-nav trigger
                  // with no hover override, so it fell through to the ghost default — fine when
                  // that was `panel-hover`, but now it's `accent`, which is near-white against
                  // the nav and reads as no hover at all.
                  'hover:bg-sidebar-active/60',
                )}
                aria-label={t('Help & resources')}
              >
                <HelpIcon />
                {!hasSeen && (
                  // Primary-green "unseen" dot — positioned slightly outside the
                  // button's top-right corner (matches the notification badge's
                  // offset pattern) so it sits clearly above/right of the icon
                  // instead of overlapping it.
                  <span
                    className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-muted"
                    aria-hidden
                    data-testid="help-dropdown-unseen-dot"
                  />
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('Help & resources')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent
        align="end"
        // Tighter to the trigger — smaller vertical gap and a small
        // negative alignOffset shifts the popup's right edge a few px
        // past the icon's right edge so the menu visually reaches toward
        // the bell area instead of stopping short of it.
        sideOffset={4}
        alignOffset={-8}
        className="w-64"
        data-testid="help-dropdown-content"
      >
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('Help & resources')}
        </DropdownMenuLabel>
        {/* Watch demo — opens the Wistia "Getting started" video in a modal.
            Sits at the top of the menu since video is the lowest-friction
            way for new users to learn the product. */}
        <DropdownMenuItem
          data-testid="help-dropdown-watch-demo"
          onSelect={(e) => {
            // Prevent the menu from closing before the dialog mounts —
            // we want the user to see the video, not the menu re-open.
            e.preventDefault();
            setVideoId(GETTING_STARTED_VIDEO_ID);
          }}
          className="flex cursor-pointer items-center gap-3"
        >
          <PlayCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex-1 text-sm">{t('Quick start video')}</span>
        </DropdownMenuItem>
        {stepByStep && (
          <ResourceItem
            label={t(stepByStep.text)}
            href={stepByStep.url}
            icon={GraduationCap}
            testId="help-dropdown-step-by-step"
          />
        )}
        {documentation && (
          // Label overridden to "Knowledge Base" (the backend text is
          // "Documentation" — keeping the URL but renaming the menu item).
          <ResourceItem
            label={t('Knowledge Base')}
            href={documentation.url}
            icon={BookOpen}
            testId="help-dropdown-docs"
          />
        )}
        {bookACall && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              {t('Still need help?')}
            </DropdownMenuLabel>
            <ResourceItem
              label={t('Book a call')}
              href={bookACall.url}
              icon={Headphones}
              testId="help-dropdown-book-call"
            />
          </>
        )}
      </DropdownMenuContent>
      <WistiaDialog videoId={videoId} onClose={() => setVideoId(null)} />
    </DropdownMenu>
  );
}

/**
 * Single menu row — muted icon on the left, label in the middle, external-
 * link arrow on the right. Matches Claude's "Learn more" menu and Notion's
 * help menu: compact, no card chrome.
 */
function ResourceItem({
  label,
  href,
  icon: Icon,
  testId,
}: {
  label: string;
  href: string;
  icon: LucideIcon;
  testId: string;
}) {
  return (
    <DropdownMenuItem asChild data-testid={testId}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex cursor-pointer items-center gap-3"
      >
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="flex-1 text-sm">{label}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </a>
    </DropdownMenuItem>
  );
}

/** "?" icon — slightly bigger than other top-nav icons to match the bell's visual weight. */
function HelpIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}
